import { api } from './apiService.js';
import { handleError } from './handleError.js';
import defaultAvatar from '../images/default-avatar.svg';
import {
    formatDate,
    formatPhoneNumberForDisplay,
    formatPhoneNumberForInput, getCountryFlag, getCountryName,
    normalizePhoneNumber,
    parseUserAgent, toPersianDigits
} from "./utils";
import { logout } from "./session.js";
import {ALERT_TYPES, showAlert} from "./alert";

import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import XHRUpload from '@uppy/xhr-upload';
import Persian from '@uppy/locales/lib/fa_IR';
import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';
import {updateTopbarUI} from "./layout";

const alertID = 'profile-alert';

export const statusMap = {
    None: { text: 'نامشخص', color: 'secondary' },
    Available: { text: 'در دسترس', color: 'success' },
    Emergency: { text: 'اضطراری', color: 'warning' },
    Busy: { text: 'مشغول', color: 'danger' },
    Away: { text: 'خارج از محل‌کار', color: 'info' }
};

function updateButtonUI(status) {
    const statusButton = document.getElementById('profile-main-status');
    const statusInfo = statusMap[status] || statusMap['None'];
    statusButton.textContent = statusInfo.text
    statusButton.innerHTML += '<i class="las la-angle-left me-1"></i>';
    statusButton.className = `btn btn-${statusInfo.color} dropdown-toggle`;
}

function populateAvatarData(){
    const mainAvatar = document.getElementById('profile-main-avatar');
    if (mainAvatar) mainAvatar.src = defaultAvatar;
    api.userGetProfileAvatar()
        .then(avatarBlob => {
            const avatarUrl = URL.createObjectURL(avatarBlob);
            if (mainAvatar) mainAvatar.src = avatarUrl;
        }).catch(avatarError => {});
}

function populateProfileData() {
    const mainName = document.getElementById('profile-main-name');
    const mainUsername = document.getElementById('profile-main-username');
    const mainEmail = document.getElementById('profile-main-email');
    const mainPhone = document.getElementById('profile-main-phone');
    const nameInput = document.getElementById('profile-edit-name');
    const usernameInput = document.getElementById('profile-edit-username');
    const emailInput = document.getElementById('profile-edit-email');
    const phoneInput = document.getElementById('profile-edit-phone');

    api.userGetProfile()
        .then(userProfile => {
            if (mainName) {
                mainName.textContent = userProfile.name || 'کاربر ویکو';
            }
            if (mainUsername) {
                mainUsername.textContent = `${userProfile.username || 'ID'}`;
            }
            if (mainEmail) {
                mainEmail.textContent = userProfile.email || 'ایمیل ثبت نشده';
                mainEmail.href = `mailto:${userProfile.email || ''}`;
            }
            if (mainPhone) {
                mainPhone.textContent = formatPhoneNumberForDisplay(userProfile.phone);
                mainPhone.href = `tel:+${userProfile.phone || ''}`;
            }

            updateButtonUI(userProfile.status || 'None');

            if (nameInput) {
                nameInput.placeholder = userProfile.name || '';
                nameInput.value = ""
            }
            if (usernameInput) {
                usernameInput.placeholder = userProfile.username || '';
                usernameInput.value = ""
            }
            if (emailInput) {
                emailInput.placeholder = userProfile.email || '';
                emailInput.value = ""
            }
            if (phoneInput) {
                phoneInput.placeholder = formatPhoneNumberForInput(userProfile.phone);
                phoneInput.value = ""
            }
        }).catch(error => {
            handleError(error, alertID);
        });
}

function initStatusDropdown() {
    const statusDropdownItems = document.querySelectorAll('.status-item');
    let currentStatus = 'None';
    statusDropdownItems.forEach(item => {
        item.addEventListener('click', (event) => {
            event.preventDefault();
            document.getElementById('profile-main-status-dd').classList.remove("show");
            const newStatus = item.dataset.status;
            if (newStatus === currentStatus) return;
            const oldStatus = currentStatus;
            currentStatus = newStatus;
            updateButtonUI(newStatus);
            api.userPutStatus(newStatus)
                .catch(error => {
                    handleError(error, alertID);
                    currentStatus = oldStatus;
                    updateButtonUI(oldStatus);
                });
        });
    });
}

function initEditProfileForm() {
    const form = document.getElementById('profile-edit-form');
    if (!form) return;

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const submitButton = form.querySelector('button[type="submit"]');
        submitButton.disabled = true;

        let profileData = {};
        const nameInput = document.getElementById('profile-edit-name').value;
        const usernameInput = document.getElementById('profile-edit-username').value;
        const emailInput = document.getElementById('profile-edit-email').value;
        const phoneInput = document.getElementById('profile-edit-phone').value;
        if (nameInput) profileData.name = nameInput
        if (usernameInput) profileData.username = usernameInput
        if (emailInput) profileData.email = emailInput
        if (phoneInput) profileData.phone = normalizePhoneNumber(phoneInput);

        if (Object.keys(profileData).length === 0){
            showAlert(alertID, ALERT_TYPES.WARN, 'هشدار!', 'هیچ اطلاعاتی وارد نشده.');
            submitButton.disabled = false;
            return;
        }

        try {
            await api.userPutProfile(profileData);
            showAlert(alertID, ALERT_TYPES.OK, 'موفق!', 'اطلاعات پروفایل شما با موفقیت به‌روزرسانی شد.');
            populateProfileData();
        } catch (error) {
            handleError(error, alertID);
        } finally {
            submitButton.disabled = false;
        }
    });
}

function initChangePasswordForm() {
    const form = document.getElementById('profile-pass-form');
    if (!form) return;

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const submitButton = form.querySelector('button[type="submit"]');
        submitButton.disabled = true;

        const oldPassword = document.getElementById('profile-pass-old').value;
        const newPassword = document.getElementById('profile-pass-new').value;
        const confirmPassword = document.getElementById('profile-pass-confirm').value;

        if (!oldPassword || !newPassword || !confirmPassword){
            showAlert(alertID, ALERT_TYPES.WARN, 'هشدار!', 'لطفا تمامی اطلاعات را وارد کنید.');
            submitButton.disabled = false;
            return;
        }

        if (newPassword !== confirmPassword) {
            showAlert(alertID, ALERT_TYPES.ERROR, 'خطا!', 'رمز عبور جدید و تکرار آن با هم مطابقت ندارند.');
            submitButton.disabled = false;
            return;
        }

        const passwordData = {
            old_password: oldPassword,
            new_password: newPassword,
        };

        try {
            await api.userPutPassword(passwordData);
            showAlert(alertID, ALERT_TYPES.OK, 'حله!', 'رمز ورود تغییر کرد. لطفاً دوباره وارد شوید.');
            setTimeout(() => {
                logout();
            }, 3000);
        } catch (error) {
            handleError(error, alertID);
            submitButton.disabled = false;
        }
    });
}

function initForgotPasswordLink() {
    const forgotLink = document.getElementById('profile-pass-forget');
    if (!forgotLink) return;

    forgotLink.addEventListener('click', (event) => {
        event.preventDefault();
        logout();
        setTimeout(() => {
            window.location.href = '/forget';
        }, 1000);
    });
}

function initAvatarUpload() {
    const deleteAvatarButton = document.getElementById("profile-avatar-delete");
    if (!deleteAvatarButton) return;

    deleteAvatarButton.addEventListener("click", () => {
        deleteAvatarButton.disabled = true;
        api.userDeleteProfileAvatar()
            .then(() => {
                showAlert(alertID, ALERT_TYPES.OK, 'حله!', 'تصویر پروفایل شما حذف شد.');
                updateTopbarUI().then();
                populateAvatarData();
            })
            .catch(error => {handleError(error, alertID);})
            .finally(() => {deleteAvatarButton.disabled = false;});
    });

    const getCurrentTheme = () => document.documentElement.getAttribute('data-bs-theme') || 'dark';

    const uppy = new Uppy({
        debug: true,
        autoProceed: true,
        restrictions: {
            maxNumberOfFiles: 1,
            allowedFileTypes: ['.jpg', '.png'],
            maxFileSize: 2 * 1024 * 1024,
        },
    });

    uppy.use(Dashboard, {
        inline: true,
        target: '#avatar-upload-area',
        height: 300,
        proudlyDisplayPoweredByUppy: false,
        note: 'فقط تصاویر با فرمت JPG یا PNG و با حجم کمتر از ۲ مگابایت مجاز هستند.',
        theme: getCurrentTheme(),
        locale: Persian,
    });

    uppy.use(XHRUpload, {
        endpoint: api.userPostProfileAvatarEndpoint(),
        method: 'POST',
        formData: true,
        fieldName: 'avatar',
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
    });

    uppy.on('upload-success', () => {
        showAlert(alertID, ALERT_TYPES.OK, 'حله!', 'تصویر پروفایل شما به‌روزرسانی شد.');
        updateTopbarUI().then();
        populateAvatarData();
        const files = uppy.getFiles();
        files.forEach(file => {uppy.removeFile(file.id);});
    });

    uppy.on('upload-error', (file, error) => {
        handleError(error, alertID);
    });

    const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'data-bs-theme') {
                const newTheme = getCurrentTheme();
                uppy.getPlugin('Dashboard').setOptions({ theme: newTheme });
            }
        }
    });
    observer.observe(document.documentElement, { attributes: true });
}

function initSessionsList() {
    const tableBody = document.querySelector("#profile-session-table tbody");
    const terminateAllButton = document.getElementById("profile-session-terminate");

    if (!tableBody || !terminateAllButton) return;

    async function renderSessions() {
        try {
            const sessions = await api.userGetSessions();
            tableBody.innerHTML = '';

            sessions.forEach(session => {
                const agentInfo = parseUserAgent(session.user_agent);
                const countryName = getCountryName(session.country_code);
                const flagHtml = getCountryFlag(session.country_code);
                const isCurrent = session.is_current;

                const row = document.createElement('tr');
                row.innerHTML = `
                    <td><i class="${agentInfo.os.icon} fs-3 align-middle"></i><span class="me-1">${agentInfo.os.name}</span></td>
                    <td><i class="${agentInfo.browser.icon} fs-3 align-middle"></i><span class="me-1">${agentInfo.browser.name}</span></td>
                    <td>${flagHtml}<span class="me-1">${countryName}</span></td>
                    <td>${formatDate(session.created_at)}</td>
                    <td>${formatDate(session.expires_at)}</td>
                    <td class="text-md-center">
                        ${isCurrent
                    ? '<span class="badge bg-transparent border border-primary text-primary">نشست فعلی</span>'
                    : `<a href="" class="delete-session-btn" data-session-id="${session.id}"><i class="las la-sign-out-alt fs-4 text-danger"></i></a>`
                }
                    </td>
                `;

                if (!isCurrent) {
                    row.querySelector('.delete-session-btn').addEventListener('click', (e) => {
                        e.preventDefault();
                        api.userDeleteSessionId(session.id)
                            .then(() => {
                                showAlert(alertID, ALERT_TYPES.OK, 'حله!', 'نشست مورد نظر با موفقیت غیرفعال شد.');
                                renderSessions();
                            })
                            .catch(err => handleError(err, alertID));
                    });
                }
                tableBody.appendChild(row);
            });

        } catch (error) {
            handleError(error, alertID);
            tableBody.innerHTML = '<tr><td colspan="6" class="text-center">خطا در دریافت لیست نشست‌ها</td></tr>';
        }
    }

    terminateAllButton.addEventListener('click', () => {
        api.userDeleteSessionsOthers()
            .then(() => {
                showAlert(alertID, ALERT_TYPES.OK, 'موفق!', 'تمام نشست‌های دیگر غیرفعال شدید.');
                renderSessions().then();
            })
            .catch(err => handleError(err, alertID));
    });

    renderSessions().then();
}

export function initProfilePage() {
    populateAvatarData();
    populateProfileData();
    initStatusDropdown();
    initEditProfileForm();
    initAvatarUpload();
    initChangePasswordForm();
    initForgotPasswordLink();
    initSessionsList();
}
